import { FC, isValidElement, ReactNode } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';

import Button, { ButtonProps } from 'targets/web/components/Button';

interface ActionButtonProps extends ButtonProps {
  text?: string;
  element?: ReactNode;
  loading?: boolean;
}

export interface ModalProps extends Omit<DialogProps, 'content' | 'title'> {
  /**
   * Modal title.
   */
  title?: ReactNode;
  /**
   * Modal description.
   */
  description?: ReactNode;
  /**
   * Modal content. Can be plain text or a React node.
   */
  content?: ReactNode;
  /**
   * Modal actions. It accepts an object with _primary_ and _secondary_ `Button` props or a React node.
   */
  actions?: { primary?: ActionButtonProps; secondary?: ActionButtonProps } | ReactNode;
  /**
   * Width of the modal. Defaults to `480` pixels.
   */
  width?: number | string;
  /**
   * If `true`, clicking outside the modal will not fire the `onClose` callback.
   * @default false
   */
  disableBackdropClick?: boolean;
  /**
   * Callback fired when the component requests to be closed.
   */
  onClose: () => void;

  showCloseButton?: boolean;
}

export const Modal: FC<ModalProps> = ({
  title,
  description,
  content,
  actions,
  disableBackdropClick,
  onClose,
  showCloseButton,
  ...rest
}) => (
  <Dialog
    {...rest}
    onClose={(_event, reason) => {
      if (disableBackdropClick && reason === 'backdropClick') {
        return;
      }
      return onClose && onClose();
    }}
  >
    {title && (
      <DialogTitle component="div">
        {isValidElement(title) ? (
          title
        ) : (
          <Typography variant="h5" sx={{ mb: description ? 1 : 0 }}>
            {title}
          </Typography>
        )}

        {isValidElement(description) ? (
          description
        ) : (
          <Typography color="text.secondary">{description}</Typography>
        )}
      </DialogTitle>
    )}
    {showCloseButton && (
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
    )}
    {content && (
      <DialogContent>
        {isValidElement(content) ? (
          content
        ) : (
          <Typography color="text.secondary">{content}</Typography>
        )}
      </DialogContent>
    )}
    {actions && (
      <DialogActions>
        {isValidElement(actions) ? (
          actions
        ) : actions && typeof actions === 'object' && 'primary' in actions ? (
          <>
            {actions.secondary &&
              (actions.secondary.element || (
                <Button
                  variant="outlined"
                  color="secondary"
                  {...actions.secondary}
                  {...(!actions.secondary.onClick && {
                    onClick: onClose,
                  })}
                >
                  {actions.secondary.text}
                </Button>
              ))}
            {actions.primary &&
              (actions.primary.element || (
                <LoadingButton
                  variant="contained"
                  autoFocus
                  {...actions.primary}
                  {...(!actions.primary.onClick && {
                    onClick: onClose,
                  })}
                >
                  {actions.primary.text}
                </LoadingButton>
              ))}
          </>
        ) : undefined}
      </DialogActions>
    )}
  </Dialog>
);

export default Modal;
